import { useState, useEffect } from 'react';

/**
 * Custom hook per gestire l'installazione PWA
 * Rileva la piattaforma (iOS, Android, macOS, Windows) e gestisce il prompt di installazione
 */
const useInstallPrompt = () => {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [isInstallable, setIsInstallable] = useState(false);
    const [platform, setPlatform] = useState({ os: 'unknown', browser: 'unknown' });

    // Funzione per rilevare il sistema operativo e il browser
    const detectPlatform = () => {
        const userAgent = navigator.userAgent || navigator.vendor || window.opera;
        const platform = navigator.platform || '';

        let os = 'unknown';
        let browser = 'unknown';

        // Rilevamento OS
        if (/iPhone|iPad|iPod/i.test(userAgent)) {
            os = 'ios';
        } else if (/Android/i.test(userAgent)) {
            os = 'android';
        } else if (/Mac/i.test(platform)) {
            os = 'macos';
        } else if (/Win/i.test(platform)) {
            os = 'windows';
        } else if (/Linux/i.test(platform)) {
            os = 'linux';
        }

        // Rilevamento Browser
        if (/Safari/i.test(userAgent) && !/Chrome/i.test(userAgent) && !/CriOS/i.test(userAgent)) {
            browser = 'safari';
        } else if (/Chrome/i.test(userAgent) && !/Edg/i.test(userAgent)) {
            browser = 'chrome';
        } else if (/Edg/i.test(userAgent)) {
            browser = 'edge';
        } else if (/Firefox/i.test(userAgent)) {
            browser = 'firefox';
        }

        return { os, browser };
    };

    useEffect(() => {
        // Rileva piattaforma al mount
        const detectedPlatform = detectPlatform();
        setPlatform(detectedPlatform);

        // Verifica se l'app è già in modalità standalone (già installata)
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;

        // Se è già installata, non mostriamo l'opzione di installazione
        if (isStandalone) {
            setIsInstallable(false);
            return;
        }

        // Mostriamo la voce di menu per impostazione predefinita su Mobile e Desktop
        // In questo modo l'utente può almeno vedere le istruzioni se il prompt nativo non scatta
        const supportedPlatforms = ['ios', 'android', 'macos', 'windows', 'linux'];
        if (supportedPlatforms.includes(detectedPlatform.os)) {
            setIsInstallable(true);
        }

        // Gestione evento beforeinstallprompt (Chrome, Edge, Android)
        const handleBeforeInstallPrompt = (e) => {
            // Previeni il prompt automatico
            e.preventDefault();
            // Salva l'evento per usarlo dopo
            setDeferredPrompt(e);
            setIsInstallable(true);
            console.log('Prompt di installazione nativo pronto');
        };

        // Gestione evento appinstalled
        const handleAppInstalled = () => {
            console.log('PWA installata con successo');
            setDeferredPrompt(null);
            setIsInstallable(false);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.addEventListener('appinstalled', handleAppInstalled);

        // Cleanup
        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
            window.removeEventListener('appinstalled', handleAppInstalled);
        };
    }, []);

    // Funzione per gestire l'installazione
    const handleInstall = async () => {
        if (!deferredPrompt) {
            // Se non c'è prompt nativo (iOS, o già installato), return false
            // Il chiamante dovrà mostrare le istruzioni manuali
            return false;
        }

        // Mostra il prompt nativo
        deferredPrompt.prompt();

        // Aspetta la scelta dell'utente
        const { outcome } = await deferredPrompt.userChoice;
        console.log(`User response to install prompt: ${outcome}`);

        // Cleanup
        setDeferredPrompt(null);

        return outcome === 'accepted';
    };

    // Verifica se può mostrare il prompt nativo
    const canShowPrompt = deferredPrompt !== null;

    return {
        platform,
        isInstallable,
        handleInstall,
        canShowPrompt
    };
};

export default useInstallPrompt;
